import { Code, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { useModal } from '../../../hooks/useModal';
import type { UseRowSelectionReturn } from '../../../hooks/useRowSelection';
import { mutate } from '../../../util/api/mutate';
import { useBulkUpsertProductForm } from '../hooks/useBulkUpsertProductForm';
import type { BulkUpsertProductDto } from '../types/ProductDto';
import { BulkUpdateProductForm } from './BulkUpdateProductForm';

interface BulkUpdateProductModalProps<T> {
	close: () => void;
	isOpened: boolean;
	onSuccess: () => void;
	rowSelection: UseRowSelectionReturn<T>;
	getSkuid: (key: T) => string;
	urlParams: string;
}

export function BulkUpdateProductModal<T>({
	isOpened,
	close,
	onSuccess,
	rowSelection,
	getSkuid,
	urlParams,
}: BulkUpdateProductModalProps<T>) {
	const { organisation } = useParams();

	const successModal = useModal();

	const { form, inputs } = useBulkUpsertProductForm();

	// Product attributes are branch-independent, so keys reduce to unique skuids
	const skuidKeys = Array.from(
		new Set(rowSelection.selectedKeys.map(getSkuid)),
	);

	const bulkProductMutation = mutate<BulkUpsertProductDto, number>(
		`${organisation}/product/edit/bulk?${urlParams}`,
		{
			...inputs,
			keys: skuidKeys,
			allSelected: rowSelection.allSelectedMode,
		},
	);

	const resetAndClose = () => {
		form.reset();

		close();
		successModal.close();
	};

	const complete = () => {
		onSuccess();
		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={
					<>
						<Title label={'Bulk Edit Product Information'} />
						<Code>
							{`${rowSelection.selectedCount} item${rowSelection.selectedCount === 1 ? '' : 's'} selected. `}
						</Code>
					</>
				}
				withCloseButton={true}
				closeButtonProps={{
					style: { alignSelf: 'flex-start' },
				}}
				size={'md'}
				centered
			>
				<LoadingOverlay visible={bulkProductMutation.isLoading} />
				<SmallModal
					isOpen={successModal.isOpen}
					close={complete}
					title={'Products Updated!'}
					body={`${rowSelection.selectedCount} item${rowSelection.selectedCount === 1 ? ' has' : 's have'} been updated. Click Ok to continue...`}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await bulkProductMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								successModal.open();
							}
						});
					})}
				>
					<BulkUpdateProductForm form={form} />
					<FormButtons
						close={resetAndClose}
						confirmModalProps={{
							title: 'Confirm bulk product update',
							body: (
								<>
									Update {rowSelection.selectedCount} item
									{rowSelection.selectedCount === 1 ? '' : 's'}? Click OK to
									confirm...
								</>
							),
						}}
					/>
				</form>
			</Modal>
		</>
	);
}
